/**
 * Кнопка «Инструкция» в шапке видео-проекта и окно с ней: как собирается ролик — порядок
 * Сценарий → Кадры → Видео (не порядок вкладок), словарь, кнопки каждой вкладки и что платно.
 * Текст статичный; спека `docs/superpowers/specs/2026-10-07-video-guide-design.md`.
 */
import { BookOpen } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Btn } from "@/components/project/ui";
import { Dialog } from "@/components/trends/Dialog";

const B = ({ children }: { children: ReactNode }) => <b className="font-medium text-fg">{children}</b>;

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="grid grid-cols-[20px_1fr] gap-2">
      <span className="tnum text-fg-faint">{n}.</span>
      <span>
        <B>{title}.</B> {children}
      </span>
    </li>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid gap-2">
      <h3 className="text-[13.5px] font-medium text-fg">{title}</h3>
      {children}
    </section>
  );
}

export function VideoGuide() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Btn onClick={() => setOpen(true)} data-annot="инструкция ролика">
        <BookOpen size={13} />
        Инструкция
      </Btn>
      <Dialog open={open} title="Как собирается ролик" onClose={() => setOpen(false)} wide>
        <div className="grid max-w-[880px] gap-5 px-5 py-4 text-[12.5px] leading-relaxed text-fg-muted">
          <p>
            Проект — это один вертикальный ролик примерно на 15 секунд: наше платье в стиле библиотеки референсов.
            У ролика <B>из готового кадра</B> вкладки и есть шаги, по порядку: <B>1 · Ракурсы → 2 · Видео → 3 ·
            Монтаж</B>. У ролика <B>из платья</B> шагов четыре, и порядок такой: <B>Сценарий → Кадры → Видео →
            Монтаж</B>. Сценарий — план, что происходит в каждом кадре; кадры (ракурсы) — опорные картинки; видео —
            эти картинки оживляют разные модели; монтаж — клипы склеиваются, лучший вариант выбирается.
          </p>

          <Section title="Словарь">
            <ul className="grid gap-1.5">
              <li>
                <B>Кадр</B> — кусок ролика со своей длительностью. У кадра два промпта: <i>промпт стилла</i> — какую
                картинку нарисовать, и <i>видео-промпт</i> — только движение, как оживить эту картинку. Ещё у кадра
                есть камера, переход и отметка «лицо в кадре».
              </li>
              <li>
                <B>Стилл</B> — картинка кадра, отмеченная звездой. Модель делает клип именно из неё. Без звезды клип не
                запустится.
              </li>
              <li>
                <B>Клип</B> — видео одного кадра, сделанное одной моделью.
              </li>
              <li>
                <B>Монтаж</B> — клипы всех кадров, склеенные в ролик: титр, цветокоррекция («грейд»), знак, музыка.
              </li>
            </ul>
          </Section>

          <Section title="1. Вкладка «Сценарий»">
            <ol className="grid gap-1.5">
              <Step n={1} title="Бриф и модели">
                Заполните бриф (необязательно) и выберите модели: от них зависит, насколько длинным может быть
                кадр.
              </Step>
              <Step n={2} title="Референсы">
                Проверьте 6 референсов из библиотеки стиля. Они подбираются сами, «выбрать другие» меняет набор.
              </Step>
              <Step n={3} title="Генерация">
                «Сгенерировать сценарий ≈ $0.05» — <B>платно</B>. Получится версия: логлайн, хук,
                негатив, героиня, свет и таблица кадров.
              </Step>
              <Step n={4} title="Правка">
                «Править → сохранить как версию»: появится новая версия, старая останется.
              </Step>
              <Step n={5} title="Применение">
                «Применить к кадрам» — по сценарию создадутся кадры проекта. Если у кадров уже есть генерации,
                перезаписать их нельзя — система предложит копию проекта. «Применить только поля ролика» меняет
                негатив и логлайн, кадры не трогает.
              </Step>
            </ol>
          </Section>

          <Section title="2. Вкладка «Кадры»">
            <ul className="grid gap-1.5">
              <li>
                <B>Хронология сверху.</B> Блоки кадров по длительности и итог «15,0 с» — сумма длительностей.
                «−0,5 / +0,5» меняют длительность, перетаскивание блоков — порядок кадров.
              </li>
              <li>
                <B>Раскадровка ниже.</B> На каждом кадре: «Генерация» → пресет → «Сгенерировать» — <B>платно</B>,
                стиллы рисует OpenAI. Сначала дешёвые превью, на лучшем ставите звезду — это и есть стилл; ⤴
                пересняет его в качестве 1088×1920. Здесь же правятся камера, видео-промпт, переход и «лицо».
              </li>
              <li>
                <B>Левая колонка.</B> Библиотека проекта и «Платье»: фото платья стоят в слотах «товар» и сами уходят
                в генерацию стиллов.
              </li>
            </ul>
          </Section>

          <Section title="3. Вкладка «Видео» (у ролика из кадра — «2 · Видео»)">
            <ol className="grid gap-1.5">
              <Step n={1} title="«Движение кадров»">
                Промпт для КЛИПА: что происходит в кадре за 3–4 секунды и как идёт камера. Поля правятся щелчком;
                «предложить промпт» — <B>платно</B> ≈ $0.01: композер смотрит на готовый кадр и пишет движение и
                камеру, но только в пустые поля. Задание ракурса (промпт фото) живёт на шаге «Ракурсы» — это
                разные вещи.
              </Step>
              <Step n={2} title="«Прогнать на моделях»">
                Сверху видно, какие модели выбраны; «Выбрать модели» открывает окно с таблицей (по умолчанию
                отмечен Kling 3.0 Pro — если он устраивает, окно открывать не нужно). Проверьте блокеры: кадр без стилла, длительности или
                видео-промпта показан ссылкой на этот кадр. Проверьте итог: оценка, остаток до потолка проекта, баланс
                OpenRouter. «Запустить N клипов за ≈ $X» — <B>платно</B>: клипы встают в очередь, платит воркер.
                Модель, которая не принимает лица, сама отдаёт кадры с лицом своей запасной модели.
              </Step>
              <Step n={3} title="«Клипы»">
                Таблица «кадры × модели»: статус и цена каждого клипа. В строке кадра — его картинка: это стартовый
                фрейм, с него модель и начинает движение. У клипа виден промпт, которым он снят. «Отменить» — только
                пока клип в очереди (отправленный уже оплачен); «повторить» у упавшего и «перегенерировать» у
                готового — <B>платно</B>, причём перегенерация берёт ТЕКУЩИЙ промпт движения кадра. Готовый клип при
                этом остаётся на месте, пока новый не придёт.
              </Step>
            </ol>
          </Section>

          <Section title="4. Вкладка «Монтаж» (у ролика из кадра — «3 · Монтаж»)">
            <ol className="grid gap-1.5">
              <Step n={1} title="«Монтажи»">
                Когда у модели готовы клипы всех кадров, монтаж собирается сам, бесплатно. «Собрать монтаж» — вручную:
                другая модель или клипы по кадрам вперемешку, титр, музыка, знак. «Пересобрать» — тоже бесплатно.
              </Step>
              <Step n={2} title="«Сравнение»">
                Монтажи моделей рядом, играются одним плеером; видны факт цены и стоп-кадры для проверки платья (не
                смылось ли кружево, не появился ли шлейф). Звездой отмечаете «лучший в проекте»; у него — «Финал в
                1080p», <B>платно</B>.
              </Step>
            </ol>
          </Section>

          <Section title="Деньги">
            <p>
              <B>Платно:</B> сценарий, стиллы, клипы, повтор клипа, финал 1080p. <B>Бесплатно:</B> правки, монтаж,
              пересборка. Потолок трат проекта по умолчанию $7, меняется в «Прогнать на моделях». Все траты — в
              «Журнале вызовов».
            </p>
          </Section>
        </div>
      </Dialog>
    </>
  );
}
